import { type ReactNode, useEffect, useRef } from 'react'

import { DisplayLanguage } from '@ebalance/proto/bffuser/v1/session_model_pb'
import i18n from 'i18next'

import { useCurrentSession } from '~/hooks/useCurrentSession'
import { setCachedLang } from '~/i18n'

/**
 * DisplayLanguage enum → i18n 言語コードのマッピング。
 * JAPANESE / ENGLISH 以外 (UNSPECIFIED・未知値) は `undefined` = 「確定していない」。
 * 日本語へ倒さないのは、UNSPECIFIED がメンバー言語の取得失敗やデプロイの隙間でも
 * 起きるためで、倒すと英語ユーザーの表示とキャッシュが日本語に上書きされる。
 */
function toI18nLang(lang: DisplayLanguage): string | undefined {
  switch (lang) {
    case DisplayLanguage.ENGLISH:
      return 'en'
    case DisplayLanguage.JAPANESE:
      return 'ja'
    default:
      return undefined
  }
}

type Props = {
  children: ReactNode
}

/**
 * I18nProvider — 起動時に GetCurrentSession RPC を呼び、表示言語で i18n を更新する。
 *
 * 表示言語 (`display_language`) は、ログインメンバーが eCloud の個人設定で選んだ言語
 * (未設定なら組織言語) を backend が解決した値 (spec settings/personal-settings.md §2,
 * AC-SPS-030)。deprecated の `organization_language` は読まない。
 *
 * 初期化戦略:
 * - i18n は index.ts の initI18n() で localStorage キャッシュを使って同期 init 済み
 *   （first-paint は描画をブロックしない）
 * - このコンポーネントが mount されると session RPC を 1 回呼ぶ
 * - 画面読み込みごとに、**最初に得た確定値 (JAPANESE / ENGLISH) を 1 回だけ**適用する。
 *   現在の i18n 言語と異なれば changeLanguage で更新し、localStorage キャッシュと
 *   `<html lang>` も更新する
 * - 適用後に再取得 (テロップバーの 5 分間隔) で値が変わっても適用しない。編集中の画面で
 *   文言と CSV の言語が途中で変わらないようにするためで、eCloud での切替は次回の画面
 *   読み込みで反映する (AC-SPS-030「次回 eBalance 画面表示から」)
 * - 取得失敗・タイムアウト・UNSPECIFIED の間は適用せず、localStorage 値 (無ければ日本語)
 *   のまま据え置く。その後の再取得が確定値を返したら、その最初の確定値を適用する
 *
 * 起動時 session 取得は retry:false で fail fast とし、起動を長くブロックしない。
 * （retry:false は useCurrentSession 側で明示設定。global 既定は一過性エラーのみ
 * 有限 retry する = `providers/ApiClientProvider.tsx`）
 */
export const I18nProvider = ({ children }: Props): React.ReactElement => {
  // 再取得の有無・間隔は useCurrentSession の消費者 (テロップバー) が決める。この
  // コンポーネントは再取得を起こさず、適用の契機だけを下の ref で制御する。query option
  // の詳細と「なぜ throwOnError: false が必須か」は useCurrentSession 側に集約。
  const { data } = useCurrentSession()
  // この画面読み込みで確定値を適用済みか。component の寿命 = 画面読み込みの寿命。
  const appliedRef = useRef(false)

  // `index.html` の `<html lang>` は静的な値なので、まず現在の表示言語 (initI18n が
  // キャッシュから決めた言語) に揃える。確定値が届かない間 (取得失敗・UNSPECIFIED) も、
  // 文書の言語が画面の言語と食い違わないようにするため (アクセシビリティ)。
  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [])

  useEffect(() => {
    if (appliedRef.current || data === undefined) {
      return
    }
    const lang = toI18nLang(data.displayLanguage)
    if (lang === undefined) {
      return
    }
    appliedRef.current = true
    // 現在の言語と異なる場合のみ更新（FOUC 防止 + 無駄な re-render 回避）
    if (i18n.language !== lang) {
      void i18n.changeLanguage(lang)
    }
    // SoT の表示言語を localStorage にキャッシュ（次回 first-paint 用）
    setCachedLang(lang)
    // document.documentElement.lang を更新（アクセシビリティ + テスト assertion 用）
    document.documentElement.lang = lang
  }, [data])

  // 取得前後で描画をブロックしない（children をそのまま返す）
  // eslint-disable-next-line react/jsx-no-useless-fragment -- single child wrapper is the intended design
  return <>{children}</>
}
